import React, { useState, useEffect, useRef } from 'react';
import { Landmark, Copy, Check, Upload, Loader2, Clock, MessageCircle, AlertTriangle, FileText, ExternalLink, FileDown, XCircle } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { downloadPdf } from '../lib/download';

const API = process.env.REACT_APP_BACKEND_URL;
export const receiptUrl = (r) => `${API}/api/cars/images/${r.path.replace('rtr-cars/cars/', '')}`;
const money = (v) => (v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function CopyRow({ label, value, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(value); setCopied(true); toast.success(`${label} copied`); setTimeout(() => setCopied(false), 1500); };
  return (
    <div className="flex items-start justify-between gap-3 py-1.5" data-testid={testId}>
      <div className="min-w-0"><span className="text-slate-400 text-[11px] uppercase tracking-wide block">{label}</span><span className="text-slate-100 font-mono text-sm break-all">{value}</span></div>
      <button type="button" onClick={copy} className="text-slate-400 hover:text-white flex-shrink-0 mt-3" aria-label={`Copy ${label}`} data-testid={`${testId}-copy`}>{copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}</button>
    </div>
  );
}

export function orderSubject(order, bank) {
  return {
    id: order.id, kind: 'order', reference: order.payment?.reference || `${bank?.reference_prefix || 'CIPS-RTR'}-${order.id}`, totalRmb: order.total_rmb, payment: order.payment,
    agent: order.agent_whatsapp, uploadUrl: `${API}/api/payments/${order.id}/receipt`, proformaUrl: `${API}/api/payments/${order.id}/proforma`,
    lines: [[`Goods (${order.quantity} × ¥${money(order.price_rmb)})`, order.price_rmb * order.quantity], ...(order.domestic_shipping_rmb > 0 ? [['Domestic shipping', order.domestic_shipping_rmb]] : [])],
    waText: `Hello, I uploaded my Stanbic CIPS receipt for order #${order.id}. Please verify. Thank you.`,
  };
}

export function batchSubject(batch) {
  return {
    id: batch.id, kind: 'batch', reference: batch.reference, totalRmb: batch.total_rmb, agent: batch.agent_whatsapp,
    payment: batch.status === 'open' ? null : { status: batch.status, receipt: batch.receipt, submitted_at: batch.submitted_at, reject_note: batch.reject_note },
    uploadUrl: `${API}/api/payments/batches/${batch.id}/receipt`, proformaUrl: `${API}/api/payments/batches/${batch.id}/proforma`,
    lines: batch.orders.map((o) => [`#${o.id} · ${o.product_title?.slice(0, 40)}${o.product_title?.length > 40 ? '…' : ''} (×${o.quantity})`, o.total_rmb]),
    waText: `Hello, I uploaded my Stanbic CIPS receipt for combined payment ${batch.reference} (${batch.orders.length} orders). Please verify. Thank you.`,
  };
}

export default function StanbicCipsPanel({ subject, authHeaders, onUpdated, onCancel }) {
  const [bank, setBank] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const inputRef = useRef(null);
  const { id, reference, totalRmb, payment, lines, uploadUrl, proformaUrl, waText } = subject;
  const agent = subject.agent || '233544205844';

  useEffect(() => {
    fetch(`${API}/api/payments/bank-details`).then((r) => r.json()).then((d) => d.success && setBank(d.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error('Please select your receipt file');
    setUploading(true);
    const waWin = window.open('', '_blank');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(uploadUrl, { method: 'POST', headers: authHeaders(), body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      toast.success('Receipt uploaded — awaiting verification');
      onUpdated(data.data);
      setFile(null);
      if (waWin) waWin.location.href = data.whatsapp_url; else window.location.href = data.whatsapp_url;
    } catch (err) { if (waWin) waWin.close(); toast.error(err.message); }
    finally { setUploading(false); }
  };

  const proforma = async () => { setDownloading(true); await downloadPdf(proformaUrl, authHeaders(), `Proforma-${reference}.pdf`); setDownloading(false); };

  const awaiting = payment?.status === 'awaiting_verification';
  const rejected = payment?.status === 'rejected';

  return (
    <div className="rounded-2xl p-5 text-white" style={{ background: '#0d1b2a', border: '1px solid #1e3a8a' }} data-testid={`cips-panel-${id}`}>
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-700 pb-4 mb-4">
        <div className="bg-blue-900 px-3 py-2 rounded-lg font-black text-sm tracking-wide flex items-center gap-2"><Landmark className="w-4 h-4" /> STANBIC</div>
        <div className="flex-1 min-w-[180px]">
          <h3 className="font-bold text-slate-50 text-base">{bank?.method_label || 'Stanbic Bank Direct CIPS Transfer'}</h3>
          <p className="text-xs text-slate-400">{subject.kind === 'batch' ? `Combined payment for ${lines.length} orders` : 'Direct CIPS clearing from your Stanbic Enterprise account'}</p>
        </div>
        <Button type="button" variant="outline" onClick={proforma} disabled={downloading} className="bg-transparent border-slate-600 text-slate-100 hover:bg-slate-800 hover:text-white text-xs rounded-lg" data-testid={`cips-proforma-${id}`}>
          {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileDown className="w-4 h-4 mr-1" />} Proforma Invoice PDF
        </Button>
      </div>

      <div className="bg-slate-800 rounded-xl p-4 mb-4 space-y-2 text-sm" data-testid={`cips-proforma-summary-${id}`}>
        <div className="flex justify-between gap-3"><span className="text-slate-400">Payment Reference</span><strong className="text-sky-400 font-mono" data-testid={`cips-reference-${id}`}>{reference}</strong></div>
        {lines.map(([label, val], i) => <div key={i} className="flex justify-between gap-3"><span className="text-slate-400 truncate">{label}</span><span className="whitespace-nowrap">¥ {money(val)}</span></div>)}
        <div className="flex justify-between border-t border-slate-700 pt-2"><span className="text-slate-300 font-semibold">Total RMB Amount</span><strong className="text-emerald-400" data-testid={`cips-total-${id}`}>¥ {money(totalRmb)} CNY</strong></div>
        <div className="flex justify-between"><span className="text-slate-400">Est. Stanbic Settlement (GHS)</span><span className="text-yellow-400">Calculated in Stanbic App</span></div>
      </div>

      {awaiting ? (
        <div className="bg-amber-500/10 border border-amber-400/40 rounded-xl p-4" data-testid={`cips-awaiting-${id}`}>
          <p className="font-bold text-amber-300 flex items-center gap-2"><Clock className="w-5 h-5" /> Awaiting Payment Verification</p>
          <p className="text-sm text-amber-100/80 mt-1">Receipt <span className="font-mono">{payment.receipt?.filename}</span> uploaded {new Date(payment.submitted_at).toLocaleString()}. Our Shanghai finance team will confirm the RMB arrival and start your order{subject.kind === 'batch' ? 's' : ''}.</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {payment.receipt && <a href={receiptUrl(payment.receipt)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs bg-slate-800 hover:bg-slate-700 px-3 py-2 rounded-lg" data-testid={`cips-view-receipt-${id}`}><FileText className="w-4 h-4" /> View receipt</a>}
            <a href={`https://wa.me/${agent}?text=${encodeURIComponent(waText)}`} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs bg-emerald-600 hover:bg-emerald-500 px-3 py-2 rounded-lg font-semibold" data-testid={`cips-notify-agent-${id}`}><MessageCircle className="w-4 h-4" /> Notify agent on WhatsApp</a>
          </div>
        </div>
      ) : (
        <>
          {rejected && (
            <div className="bg-red-500/10 border border-red-400/40 rounded-xl p-3 mb-4 text-sm" data-testid={`cips-rejected-${id}`}>
              <p className="font-bold text-red-300 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Receipt could not be verified</p>
              <p className="text-red-100/80 mt-1">{payment.reject_note} — please upload a valid receipt below.</p>
            </div>
          )}
          <div className="text-sm text-slate-300 mb-4">
            <h4 className="text-sky-400 font-semibold mb-2">Transfer instructions (Stanbic Enterprise App)</h4>
            <ol className="list-decimal pl-5 space-y-1 leading-relaxed">
              <li>Log in to <strong className="text-slate-100">Stanbic Enterprise Online / Mobile Banking</strong>.</li>
              <li>Select <strong className="text-slate-100">International Payments</strong> → Currency: <strong className="text-slate-100">{bank?.account_currency || 'USD'}</strong>.</li>
              <li>Beneficiary name: <strong className="text-slate-100">{bank?.beneficiary_name}</strong>.</li>
              <li><strong className="text-amber-300">MANDATORY:</strong> put <mark className="bg-blue-900 text-sky-300 px-1.5 py-0.5 rounded font-mono">{reference}</mark> in the <em>Payment Memo / Reference</em> field.</li>
            </ol>
          </div>

          <div className="rounded-xl p-4 mb-4" style={{ background: '#0f172a', border: '1px dashed #334155' }} data-testid={`cips-bank-card-${id}`}>
            {!bank ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : (
              <>
                <CopyRow label="Beneficiary" value={bank.beneficiary_name} testId={`cips-beneficiary-${id}`} />
                {bank.beneficiary_name_en && <p className="text-[11px] text-slate-500 -mt-1 mb-1">{bank.beneficiary_name_en}</p>}
                <CopyRow label={`Account No (${bank.account_currency})`} value={bank.account_number} testId={`cips-account-${id}`} />
                <CopyRow label="Beneficiary Bank" value={bank.bank_name} testId={`cips-bank-${id}`} />
                {bank.bank_name_en && <p className="text-[11px] text-slate-500 -mt-1 mb-1">{bank.bank_name_en}</p>}
                <CopyRow label="SWIFT / CIPS Code" value={bank.swift_code} testId={`cips-swift-${id}`} />
                <CopyRow label="Payment Reference" value={reference} testId={`cips-ref-${id}`} />
              </>
            )}
          </div>

          <form onSubmit={submit} className="space-y-3" data-testid={`cips-receipt-form-${id}`}>
            <label className="text-sm font-semibold text-slate-50 block">Upload Stanbic transfer receipt / payment advice (PDF or image)</label>
            <input ref={inputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid={`cips-file-input-${id}`} />
            <button type="button" onClick={() => inputRef.current?.click()} className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg text-sm text-left" style={{ background: '#0f172a', border: '1px solid #334155' }} data-testid={`cips-choose-file-${id}`}>
              <span className={file ? 'text-slate-100' : 'text-slate-400'}>{file ? file.name : 'Choose a file…'}</span><Upload className="w-4 h-4 text-slate-400" />
            </button>
            <Button type="submit" disabled={uploading || !file} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-lg" data-testid={`cips-submit-receipt-${id}`}>
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />} Submit Receipt for Verification
            </Button>
          </form>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1">
            <a href={`https://wa.me/${agent}?text=${encodeURIComponent(`Hello, I need help paying ${reference}.`)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white" data-testid={`cips-help-agent-${id}`}>
              <MessageCircle className="w-3.5 h-3.5" /> Prefer another method? Chat with your agent <ExternalLink className="w-3 h-3" />
            </a>
            {onCancel && <button type="button" onClick={onCancel} className="inline-flex items-center gap-1 text-xs text-red-300 hover:text-red-200" data-testid={`cips-cancel-batch-${id}`}><XCircle className="w-3.5 h-3.5" /> Cancel combined payment</button>}
          </div>
        </>
      )}
    </div>
  );
}
